import type { JSX, ReactNode } from "react";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import styled from "styled-components";

import { Checkbox } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import categoryService, { CATEGORY_PREFIX } from "app/common/services/categoryService";
import type { ICategory } from "app/common/services/categoryService.types";
import { chosenSites } from "app/common/services/chosenSites";
import { swSettings } from "app/common/services/swSettings";
import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import { CHART_COLORS } from "app/constants/ChartColors";
import type { SelectorHandlers } from "app/components/filters-bar/useSelectorTrigger";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { MMXAlertWithPlainTooltip } from "app/components/MMXAlertWithPlainTooltip";
import { getNoCategoryItem } from "app/components/React/CategoriesDropdown/CategoryPicker";
import { translate } from "app/filters/i18nFilter";
import { minAbbrNumberFilter, minVisitsAbbrFilter } from "app/filters/ngFilters";
import { ChannelsOverviewBarChart } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewBarChart/ChannelsOverviewBarChart";
import {
    getChartConfig,
    NoDataWrapperComp,
} from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewBarChart/ChannelsOverviewBarChartConfig";
import {
    BoxSubtitleWrapper,
    LoaderWrapper,
    MMXAlertWrapper,
    RightSection,
    StyledBoxContainer,
    StyledHeaderTitle,
    TitleBox,
    TotalTrafficNumber,
    TotalTrafficTitle,
    ViewByDomainChrtContainer,
} from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewBarChart/StyledComponents";
import { Loader } from "app/pages/website-analysis/traffic-sources/mmx/components/common/Loader";
import { PngDownload } from "app/pages/website-analysis/traffic-sources/mmx/components/common/PngDownload";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import CountryService from "app/services/CountryService";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";
import utils from "app/Shared/utils";
import StyledBoxSubtitle from "pro-features/styled components/StyledBoxSubtitle/src/StyledBoxSubtitle";
import { CenteredFlexRow, FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import { ChannelsOverviewBarChartCategoryBenchmark } from "./ChannelsOverviewBarChartCategoryBenchmark";
import { percentDataFormat, dataModeMap } from "./ChannelsOverviewBarChartContainerFormat";
import { ChannelsOverviewBarChartSwitcher, useDataFormat } from "./ChannelsOverviewBarChartSwitcher";
import { ChannelsOverviewBarChartTopLegends } from "app/components/React/ChannelsOverviewBarChartTopLegends/ChannelsOverviewBarChartTopLegends";
import ViewByDomainChart from "./components/view-by-domain-chart/ViewByDomainChart";
import ViewBySwitcher, { EViewBySwitcherItems } from "./components/view-by-switcher/ViewBySwitcher";
import type { IChannelsData, ILegendItem, ITrafficSources } from "./types";
import { TotalTrafficSourceDisplayMap } from "./types";
import { processDataToShow } from "./utils/chartHelpers";

const noCategoryItem = getNoCategoryItem("mmx.channelsoverview.category.dropdown.placeholder");

interface IChannelsOverviewBarChartContainer extends SelectorHandlers {
    ctaComponent?: ReactNode;
    isCategoryBenchmarkVisible: boolean;
}

const ChartWrapper = styled.div`
    flex: 1;
`;

const LegendsSingleWrapper = styled(CenteredFlexRow)`
    justify-content: flex-start;
    gap: 12px;
`;

const { Channels, Domain } = EViewBySwitcherItems;

export const ChannelsOverviewBarChartContainer: React.FC<IChannelsOverviewBarChartContainer> = ({
    isCategoryBenchmarkVisible,
    ctaComponent,
    onDateClick,
    onCountryClick,
    onDateHover,
    onCountryHover,
    onWebSourceHover,
}): JSX.Element => {
    const fetchService = DefaultFetchService.getInstance();
    const { params } = useCurrentStateAndParams();
    const { country, webSource, duration, comparedDuration, category, isWWW, key, unboundedVisits } = params;
    const isMobileWeb = webSource === devicesTypes.MOBILE;
    const isTotalWeb = webSource === devicesTypes.TOTAL;
    const component = swSettings.current;
    const newMMXAlgoStartData = component.resources.NewAlgoMMX;
    const durationObject = useMemo(
        () => DurationService.getDurationData(duration, comparedDuration),
        [duration, comparedDuration],
    );
    const fromDate = durationObject.raw.from;
    const toDate = durationObject.raw.to;
    const { from, to, isWindow } = durationObject.forAPI;
    const is28d = duration === "28d";
    const isCompare = key.split(",").length > 1;
    const includeSubDomains = isWWW === "*";
    const timeGranularity = is28d ? "Daily" : "Monthly";
    const isShowMMXAlertBell = () =>
        (isMobileWeb || isTotalWeb) &&
        fromDate.isBefore(dayjs(newMMXAlgoStartData)) &&
        toDate.isAfter(dayjs(newMMXAlgoStartData));

    const [data, setData] = useState<IChannelsData>(null);
    const [dataToShow, setDataToShow] = useState<any>(null);
    const [unbounded, setUnbounded] = useState<boolean>(unboundedVisits === "true" ? true : false);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isError, setIsError] = useState<boolean>(false);
    const [viewBy, setViewBy] = useState<number>(() => {
        const { channelsOverviewViewByType } = proNavigator.getParams();
        if (!isCompare) {
            return Channels;
        } else {
            return channelsOverviewViewByType === EViewBySwitcherItems[Domain].toString() ? Domain : Channels;
        }
    });
    const [legends, setLegends] = useState<ILegendItem[]>(
        chosenSites.sitelistForLegend().map((item) => ({
            name: item.name,
            color: item.color,
            visible: true,
            data: null,
        })),
    );
    const chartRef = useRef<HTMLElement>(null);
    const [dataFormat, setDataFormat] = useDataFormat(isCompare);
    const [selectedCategory, setSelectedCategory] = useState<ICategory>(
        category && categoryService.getCategory(category),
    );
    const isCategorySelected = useMemo(
        () => selectedCategory && selectedCategory.id !== noCategoryItem.id,
        [selectedCategory],
    );
    const hideLegends = !isCategorySelected && !isCompare;

    useEffect(() => {
        // if we have data and webSource is DESKTOP
        // the user can select to see unbounded visits only in the component
        if (data) {
            if (!isEmpty(data) && webSource === devicesTypes.DESKTOP) {
                if (unbounded) {
                    setDataToShow(data[1]);
                } else {
                    if (data.category) {
                        setDataToShow({ ...data[0], category: data.category });
                    } else {
                        setDataToShow(data[0]);
                    }
                }
            } else {
                setDataToShow(data);
            }
        }
    }, [unbounded, data]);

    useEffect(() => {
        async function fetchData() {
            setIsLoading(true);
            setData(null);
            setDataToShow(null);

            const getEndpointAndExtract = () => {
                if (isTotalWeb) {
                    return {
                        url: `widgetApi/MarketingMixTotal/TrafficSourcesOverview/PieChart`,
                        extract: (res: any) => res.Data.Total,
                    };
                }
                const method = isMobileWeb ? `TrafficSourcesOverview` : `TrafficSourcesOverviewBounce`;
                return {
                    url: `widgetApi/MarketingMix/${method}/PieChart`,
                    extract: (res: any) => res.Data,
                };
            };
            const { url, extract } = getEndpointAndExtract();
            const dataPromise = fetchService
                .get<{ Data: object[] }>(url, {
                    country,
                    from,
                    includeSubDomains,
                    isWindow,
                    keys: key,
                    timeGranularity,
                    to,
                    webSource,
                })
                .then(extract);

            const promises = [dataPromise];

            if (isCategorySelected) {
                const industryKey = CATEGORY_PREFIX + selectedCategory.id;
                const industryDataPromise = fetchService
                    .get<{ Data: object[] }>("api/WidgetKpis/TrafficSourcesOverviewAverage/GetPieChartData", {
                        country,
                        from,
                        includeSubDomains,
                        isWindow,
                        keys: industryKey,
                        timeGranularity,
                        to,
                        webSource,
                    })
                    .then((industryResponse: any) => industryResponse.Data[industryKey]);

                promises.push(industryDataPromise);
            }
            try {
                const [mmXTrafficSources, industryTrafficSources] = await Promise.all(promises);
                const channelsData = transformData(mmXTrafficSources, industryTrafficSources);
                setData(channelsData);
            } catch (e) {
                setIsError(true);
            } finally {
                setIsLoading(false);
            }
        }

        fetchData();
    }, [from, to, isWindow, country, webSource, key, selectedCategory]);

    useEffect(() => {
        if (dataToShow) {
            const updatedLegends = [];
            legends.forEach((legend) => {
                if (legend?.isCategory) {
                    if (isCategorySelected) {
                        updatedLegends.push(legend);
                    }
                } else {
                    updatedLegends.push({
                        ...legend,
                        data: dataToShow?.[legend.name]?.totalVisits
                            ? minVisitsAbbrFilter()(dataToShow?.[legend.name]?.totalVisits)
                            : "N/A",
                    });
                }
            });

            setLegends(updatedLegends);
        }
    }, [dataToShow]);

    useEffect(() => {
        // getting JQuery element
        const $chart = $(chartRef.current);
        $chart.on("click", "[data-ts-category]", (evt) => {
            const trafficSource = evt.target.getAttribute("data-ts-category");
            allTrackers.dispatchTrackEvent("Internal Link", "click", `Traffic Sources Overview/${trafficSource}`);
        });

        return () => {
            $chart.off("click", "[data-ts-category]");
        };
    }, []);

    const addCategoryLegend = (name, total) => {
        const categoryLegend = {
            data: minAbbrNumberFilter()(total),
            visible: true,
            name,
            color: "rgb(216, 220, 232)",
            isCategory: true,
        };

        setLegends([...legends.filter((l) => !l.isCategory), categoryLegend]);
    };

    const channelColorMap = CHART_COLORS.trafficSourcesColorsBySourceMMX;

    const seriesData = useMemo(
        () =>
            processDataToShow(
                dataToShow,
                key,
                legends,
                dataFormat,
                isCompare,
                chosenSites,
                selectedCategory,
                channelColorMap,
            ).channels,
        [dataToShow, key, legends, dataFormat, isCompare, chosenSites, selectedCategory, channelColorMap],
    );

    const config = useMemo(
        () =>
            dataToShow &&
            getChartConfig({
                data: dataToShow,
                dataFormat,
                isCompare,
                isBenchmarkMode: isCategorySelected,
                showWatermark: false,
            }),
        [dataToShow, webSource, isCompare, key, dataFormat, isCategorySelected],
    );

    const subtitleFilters = useMemo(
        () => [
            {
                filter: "date",
                value: {
                    from: fromDate.valueOf(),
                    to: toDate.valueOf(),
                    useRangeDisplay: duration !== "28d" && fromDate.format("YYYY-MM") !== toDate.format("YYYY-MM"),
                },
                onClick: onDateClick,
                onMouseEnter: onDateHover,
            },
            {
                filter: "country",
                countryCode: country,
                value: CountryService.getCountryById(country)?.text,
                onClick: onCountryClick,
                onMouseEnter: onCountryHover,
            },
            {
                filter: "webSource",
                value: webSource,
                onMouseEnter: onWebSourceHover,
            },
        ],
        [
            fromDate,
            toDate,
            country,
            webSource,
            duration,
            onDateClick,
            onCountryClick,
            onDateHover,
            onCountryHover,
            onWebSourceHover,
        ],
    );

    const getTotalVisits = (totalVisitsData: ITrafficSources) => {
        return Object.values(totalVisitsData).reduce((sum: number, val) => sum + val, 0);
    };

    const checkAreTrafficSourcesEmpty = (mmXTrafficSources) => {
        let areTrafficSourcesEmpty = true;
        const trafficSourcesValues =
            webSource === devicesTypes.MOBILE || isTotalWeb ? mmXTrafficSources : mmXTrafficSources.Total;
        if (trafficSourcesValues) {
            const sourcesValuesArray = Object.values(trafficSourcesValues);
            let i = 0;
            while (i < sourcesValuesArray.length && areTrafficSourcesEmpty) {
                // check each site, if sourcesValuesArray[i] is null we dont have data
                if (sourcesValuesArray[i]) {
                    // check at least one cnannel have data
                    const channelValuesArray = Object.values(sourcesValuesArray[i]);
                    if (channelValuesArray.some((item) => item > 0)) {
                        areTrafficSourcesEmpty = false;
                    }
                }
                i++;
            }
        }
        return areTrafficSourcesEmpty;
    };

    const onEngagedByVisitsOnlyCheckBoxClick = () => {
        allTrackers.dispatchTrackEvent(
            "Check box",
            unbounded ? "remove" : "add",
            "Channels Overview /Only unbounced visits",
        );
        proNavigator.updateQueryParams(
            { unboundedVisits: !unbounded, category: noCategoryItem.id },
            {
                reload: false,
            },
        );
        if (!unbounded) {
            setLegends(legends.filter((legend) => !legend.isCategory));
            setSelectedCategory(undefined);
        }
        setUnbounded(!unbounded);
    };

    const transformData = (mmXTrafficSources, industryTrafficSources): IChannelsData => {
        const sitesDataWithTotalVisits = checkAreTrafficSourcesEmpty(mmXTrafficSources)
            ? {}
            : webSource !== devicesTypes.DESKTOP || isTotalWeb
              ? Object.entries(mmXTrafficSources).reduce((newDataObject, [domain, trafficSources]) => {
                    trafficSources = trafficSources || {};
                    const totalVisits = getTotalVisits(trafficSources as ITrafficSources);

                    return {
                        ...newDataObject,
                        [domain]: {
                            trafficSources: trafficSources,
                            totalVisits: totalVisits,
                        },
                    };
                }, {})
              : Object.keys(mmXTrafficSources).map((state) =>
                    Object.entries(mmXTrafficSources[state]).reduce((newDataObject, [domain, trafficSources]) => {
                        trafficSources = trafficSources || {};
                        const totalVisits = getTotalVisits(trafficSources as ITrafficSources);

                        return {
                            ...newDataObject,
                            [domain]: {
                                trafficSources: trafficSources,
                                totalVisits: totalVisits,
                            },
                        };
                    }, {}),
                );

        const res: { Data: IChannelsData } = {
            Data: {
                ...sitesDataWithTotalVisits,
            },
        };

        if (industryTrafficSources) {
            const total = getTotalVisits(industryTrafficSources);
            res.Data.category = {
                ...selectedCategory,
                trafficSources: industryTrafficSources,
                totalVisits: total,
            };

            addCategoryLegend(selectedCategory.text, total);
        }
        if (isEmpty(res.Data)) return {};
        if (isTotalWeb) return res.Data;
        return utils.fillMissingTrafficForMMXPage(res.Data, isMobileWeb, from, isMobileWeb);
    };

    const onLegendClick = (filter) => {
        const action = filter.visible ? "remove" : "click";
        const filterSites = legends.map((f) => {
            if (f.name === filter.name) {
                f.visible = !f.visible;
            }
            return f;
        });

        setLegends(filterSites);
        allTrackers.dispatchTrackEvent("Click Filter", action, `Bar Chart/Traffic Sources Overview/${filter.name}`);
    };

    const getLegends = () =>
        legends &&
        legends.length > 0 && (
            <ChannelsOverviewBarChartTopLegends
                legendItems={legends}
                onLegendsChange={onLegendClick}
            />
        );

    const engagedByVisitsOnlyCheckbox = webSource === devicesTypes.DESKTOP && !isTotalWeb && (
        <Checkbox
            selected={unbounded}
            label={translate("mmx.channelsoverview.bar.chart.unbounced.text")}
            onClick={onEngagedByVisitsOnlyCheckBoxClick}
            size="xs"
        />
    );

    const getDomainsComponent = () => {
        return !dataToShow || isEmpty(dataToShow) || isError ? (
            <NoDataWrapperComp size="large" />
        ) : (
            <ViewByDomainChrtContainer>
                <ViewByDomainChart
                    data={dataToShow}
                    isPercentageDataMode={dataModeMap[dataFormat] === dataModeMap[percentDataFormat]}
                    childrenAboveLegend={engagedByVisitsOnlyCheckbox}
                    trafficSourcesMap={isTotalWeb ? TotalTrafficSourceDisplayMap : undefined}
                />
            </ViewByDomainChrtContainer>
        );
    };

    const renderCategoryBenchmarkDropdown = (
        <ChannelsOverviewBarChartCategoryBenchmark
            isUnbound={unbounded}
            categoryParam={category}
            dataMode={dataModeMap[dataFormat]}
            durationParam={duration}
            onCategorySelected={(category) => setSelectedCategory(category)}
        />
    );

    const getChannelsComponent = () => {
        if (!dataToShow || isEmpty(dataToShow) || isError) {
            return <NoDataWrapperComp size="large" />;
        }

        const topControls =
            isCategoryBenchmarkVisible || engagedByVisitsOnlyCheckbox ? (
                <Flex
                    gap="16px"
                    alignItems="center"
                >
                    {isCategoryBenchmarkVisible ? renderCategoryBenchmarkDropdown : null}
                    {engagedByVisitsOnlyCheckbox}
                </Flex>
            ) : null;

        const totalTrafficSingleRow =
            hideLegends && dataToShow?.[key]?.totalVisits ? (
                <LegendsSingleWrapper>
                    <TotalTrafficTitle>
                        {webSource === devicesTypes.DESKTOP
                            ? translate("mmx.channelsoverview.bar.chart.total.traffic.title.desktop")
                            : null}
                        {isTotalWeb ? translate("mmx.channelsoverview.bar.chart.total.traffic.title.total") : null}
                        {isMobileWeb ? translate("mmx.channelsoverview.bar.chart.total.traffic.title.mobile") : null}
                    </TotalTrafficTitle>
                    <TotalTrafficNumber>{minAbbrNumberFilter()(dataToShow[key].totalVisits)}</TotalTrafficNumber>
                </LegendsSingleWrapper>
            ) : null;

        return (
            <Flex
                flexDirection="column"
                gap="16px"
                padding="24px"
                data-automation="channelsOverviewChannelsView"
            >
                {topControls}
                {hideLegends ? totalTrafficSingleRow : getLegends()}
                <ChartWrapper>
                    <ChannelsOverviewBarChart
                        data={seriesData}
                        config={config}
                        dataAutomation="ChannelsOverviewBarChart"
                        stackColumns={false}
                    />
                </ChartWrapper>
                {ctaComponent}
            </Flex>
        );
    };

    const viewBySwitcherClick = (selectedViewBy: EViewBySwitcherItems) => {
        setViewBy(selectedViewBy);
    };

    return (
        <StyledBoxContainer
            ref={chartRef}
            data-automation={"channelsOverviewContainer"}
        >
            <TitleBox>
                <FlexColumn>
                    <StyledHeaderTitle>
                        <BoxTitle
                            tooltip={translate("mmx.trafficSources.bar.tooltip")}
                            customElement={
                                isShowMMXAlertBell() ? (
                                    <MMXAlertWrapper>
                                        <MMXAlertWithPlainTooltip />
                                    </MMXAlertWrapper>
                                ) : null
                            }
                        >
                            {translate("mmx.trafficSources.bar.title")}
                        </BoxTitle>
                    </StyledHeaderTitle>
                    <BoxSubtitleWrapper>
                        <StyledBoxSubtitle>
                            <BoxSubtitle filters={subtitleFilters} />
                        </StyledBoxSubtitle>
                    </BoxSubtitleWrapper>
                </FlexColumn>
                <RightSection className={"hidden-print"}>
                    {isCompare ? (
                        <ViewBySwitcher
                            onViewChange={viewBySwitcherClick}
                            selectedView={viewBy}
                        />
                    ) : null}
                    <ChannelsOverviewBarChartSwitcher
                        onClick={(selectedDataFormat) => setDataFormat(selectedDataFormat)}
                        dataFormat={dataFormat}
                    />
                    <PngDownload
                        selectedMetricName={translate("mmx.trafficSources.bar.title")}
                        chartRef={chartRef}
                    />
                    <AddToDashboard
                        webSource={webSource}
                        type="BarChartMMXDashboard"
                        metric="TrafficSourcesOverview"
                        modelType="fromWebsite"
                        filters={{
                            includeSubDomains,
                            timeGranularity,
                        }}
                        overrideAddToDashboardParams={{
                            dataMode: dataModeMap[dataFormat],
                        }}
                        buttonProps={
                            viewBy === EViewBySwitcherItems.Domain
                                ? {
                                      isDisabled: true,
                                      tooltipContent: "mmx.channelsoverview.addtodashboard.bydomain.disabled.tooltip",
                                  }
                                : undefined
                        }
                    />
                </RightSection>
            </TitleBox>
            {isLoading ? (
                <LoaderWrapper>
                    <Loader />
                </LoaderWrapper>
            ) : viewBy === EViewBySwitcherItems.Channels ? (
                getChannelsComponent()
            ) : (
                getDomainsComponent()
            )}
        </StyledBoxContainer>
    );
};
